/**
 * Pagos — métodos de pago (selector de pasarela) + transacciones.
 *
 * Flow:
 *   1. GET /api/v1/payments/providers       → lista de providers disponibles (Bold, Demo, Wompi…) con su configSchema
 *   2. GET /api/v1/payments/methods         → métodos configurados del tenant (sin secrets)
 *   3. GET /api/v1/payments/transactions    → historial
 *   4. POST/PATCH/DELETE /api/v1/payments/methods  → CRUD
 *
 * El select de pasarela renderiza dinámicamente los campos requeridos por cada provider
 * (BoldProvider.configSchema → apiKey, secretKey, mode).
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Card, CardContent, Button, Badge, PageHeader, Input, Label } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { Plus, X, Edit, Trash2, CheckCircle, AlertCircle, CreditCard, Settings, ArrowDown } from 'lucide-react'

export const Route = createFileRoute('/store/$id/payments')({
  component: StorePaymentsPage,
})

interface ProviderConfigField {
  key: string
  label: string
  type: 'text' | 'password' | 'select'
  required: boolean
  placeholder?: string
  helpText?: string
  options?: Array<{ value: string; label: string }>
}

interface ProviderInfo {
  id: string
  name: string
  description: string
  supportedMethods: string[]
  configSchema: ProviderConfigField[]
  needsWebhook: boolean
}

interface PaymentMethod {
  id: string
  name: string
  provider: string
  isActive: boolean
  hasConfig: boolean
  configKeys: string[]
  createdAt: string
}

interface Transaction {
  id: string
  orderId: string
  orderNumber: string | null
  amount: number
  currency: string
  status: 'pending' | 'completed' | 'failed' | 'refunded'
  providerTransactionId: string | null
  metadata: Record<string, unknown>
  createdAt: string
}

interface MethodsResponse { data: PaymentMethod[] }
interface ProvidersResponse { data: ProviderInfo[] }
interface TransactionsResponse {
  data: Transaction[]
  stats: { total: number; pending: number; completed: number; failed: number; refunded: number; volumeCompleted: number }
}

function StorePaymentsPage() {
  const { activeTenantId } = useAuth()
  const [tab, setTab] = useState<'methods' | 'transactions'>('methods')
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; method?: PaymentMethod } | null>(null)

  const providers = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => tenantApi.get<ProvidersResponse>('/api/v1/payments/providers'),
    enabled: !!activeTenantId,
  })
  const methods = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => tenantApi.get<MethodsResponse>('/api/v1/payments/methods'),
    enabled: !!activeTenantId,
  })
  const txs = useQuery({
    queryKey: ['payment-transactions'],
    queryFn: () => tenantApi.get<TransactionsResponse>('/api/v1/payments/transactions'),
    enabled: !!activeTenantId && tab === 'transactions',
  })

  const providerById = useMemo(() => {
    const map = new Map<string, ProviderInfo>()
    for (const p of providers.data?.data ?? []) map.set(p.id, p)
    return map
  }, [providers.data])

  return (
    <>
      <PageHeader
        title="Pagos"
        description="Configura tu pasarela y revisa transacciones"
        action={tab === 'methods' && <Button size="sm" onClick={() => setDialog({ mode: 'create' })}><Plus className="size-4 mr-2" /> Agregar método</Button>}
      />
      <div className="p-8 space-y-4">
        <div className="flex items-center gap-1.5">
          {(['methods', 'transactions'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                tab === t ? 'bg-neutral-800 text-white border-neutral-700' : 'bg-transparent text-neutral-400 border-transparent hover:text-white',
              )}
            >
              {t === 'methods' ? 'Métodos de pago' : 'Transacciones'}
            </button>
          ))}
        </div>

        {tab === 'methods' && (
          <MethodsTab
            providers={providers.data?.data ?? []}
            methods={methods.data?.data ?? []}
            isLoading={methods.isLoading || providers.isLoading}
            onEdit={(method) => setDialog({ mode: 'edit', method })}
          />
        )}

        {tab === 'transactions' && (
          <TransactionsTab
            data={txs.data}
            isLoading={txs.isLoading}
            providerById={providerById}
          />
        )}
      </div>

      {dialog && (
        <MethodDialog
          mode={dialog.mode}
          method={dialog.method}
          providers={providers.data?.data ?? []}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  )
}

function MethodsTab({ providers, methods, isLoading, onEdit }: {
  providers: ProviderInfo[]
  methods: PaymentMethod[]
  isLoading: boolean
  onEdit: (m: PaymentMethod) => void
}) {
  const providerById = useMemo(() => new Map(providers.map((p) => [p.id, p])), [providers])
  const activeMethod = methods.find((m) => m.isActive)

  if (isLoading) return <p className="text-sm text-neutral-500">Cargando...</p>

  return (
    <div className="space-y-4">
      {/* Banner del provider activo */}
      <Card className={cn('p-5 border-2', activeMethod ? 'border-emerald-700 bg-emerald-950/20' : 'border-yellow-800 bg-yellow-950/20')}>
        <div className="flex items-start gap-3">
          {activeMethod ? <CheckCircle className="size-5 text-emerald-400 shrink-0 mt-0.5" /> : <AlertCircle className="size-5 text-yellow-400 shrink-0 mt-0.5" />}
          <div className="flex-1">
            {activeMethod ? (
              <>
                <p className="font-semibold">Pasarela activa: {providerById.get(activeMethod.provider)?.name ?? activeMethod.provider}</p>
                <p className="text-sm text-neutral-400 mt-1">"{activeMethod.name}" — recibe checkouts en este momento.</p>
              </>
            ) : (
              <>
                <p className="font-semibold">Sin pasarela activa</p>
                <p className="text-sm text-neutral-400 mt-1">Configura un método de pago para empezar a recibir checkouts. El flujo de checkout fallará si no hay pasarela.</p>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Lista de métodos configurados */}
      {methods.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950 border-b border-neutral-800 text-xs text-neutral-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Nombre</th>
                  <th className="text-left px-4 py-3">Pasarela</th>
                  <th className="text-left px-4 py-3">Configuración</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {methods.map((m) => <MethodRow key={m.id} method={m} provider={providerById.get(m.provider)} onEdit={() => onEdit(m)} />)}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Catálogo de pasarelas disponibles */}
      <Card className="p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Settings className="size-4" /> Pasarelas disponibles</h3>
        <p className="text-xs text-neutral-500 mb-4">Cualquiera de estas se puede activar al crear un método. Los stubs se resuelven cuando se porte la implementación 1:1.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {providers.map((p) => (
            <div key={p.id} className="border border-neutral-800 rounded-md p-4 hover:border-neutral-700 transition">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">{p.name}</h4>
                {p.needsWebhook && <Badge variant="muted">webhook</Badge>}
              </div>
              <p className="text-xs text-neutral-500 leading-relaxed mb-2">{p.description}</p>
              <div className="flex flex-wrap gap-1">
                {p.supportedMethods.map((sm) => (
                  <span key={sm} className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-900 border border-neutral-800 text-neutral-400 uppercase">{sm}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

function MethodRow({ method, provider, onEdit }: { method: PaymentMethod; provider?: ProviderInfo; onEdit: () => void }) {
  const qc = useQueryClient()
  const remove = useMutation({
    mutationFn: () => tenantApi.delete(`/api/v1/payments/methods/${method.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-methods'] }),
  })
  const activate = useMutation({
    mutationFn: () => tenantApi.patch(`/api/v1/payments/methods/${method.id}`, { isActive: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-methods'] }),
  })

  return (
    <tr className="hover:bg-neutral-900/50">
      <td className="px-4 py-3 font-medium">{method.name}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <CreditCard className="size-3.5 text-neutral-500" />
          <span>{provider?.name ?? method.provider}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-neutral-400">
        {method.hasConfig ? `${method.configKeys.length} llave${method.configKeys.length === 1 ? '' : 's'} · ${method.configKeys.join(', ')}` : <span className="text-yellow-400">Sin configurar</span>}
      </td>
      <td className="px-4 py-3">
        {method.isActive ? <Badge variant="success">Activa</Badge> : (
          <button onClick={() => activate.mutate()} className="text-xs text-neutral-400 hover:text-white" title="Activar esta pasarela">
            Inactiva (clic para activar)
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={onEdit} className="p-1.5 rounded text-neutral-400 hover:text-white hover:bg-neutral-800" title="Editar"><Edit className="size-4" /></button>
          <button
            onClick={() => { if (confirm(`¿Eliminar "${method.name}"?`)) remove.mutate() }}
            className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-950/40"
            title="Eliminar"
          ><Trash2 className="size-4" /></button>
        </div>
      </td>
    </tr>
  )
}

function MethodDialog({ mode, method, providers, onClose }: {
  mode: 'create' | 'edit'
  method?: PaymentMethod
  providers: ProviderInfo[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState(method?.name ?? '')
  const [providerId, setProviderId] = useState(method?.provider ?? providers[0]?.id ?? '')
  const [isActive, setIsActive] = useState(method?.isActive ?? true)
  const [config, setConfig] = useState<Record<string, string>>({})

  const provider = providers.find((p) => p.id === providerId)

  const save = useMutation({
    mutationFn: (payload: { name: string; provider: string; isActive: boolean; config: Record<string, string> }) =>
      mode === 'create'
        ? tenantApi.post('/api/v1/payments/methods', payload)
        : tenantApi.patch(`/api/v1/payments/methods/${method!.id}`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payment-methods'] })
      onClose()
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    save.mutate({ name, provider: providerId, isActive, config })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-lg my-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-800">
          <h2 className="text-lg font-semibold">{mode === 'create' ? 'Agregar método de pago' : 'Editar método'}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X className="size-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <Label>Nombre interno</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Bold producción" required autoFocus />
            <p className="text-xs text-neutral-500 mt-1">Solo se ve dentro del admin.</p>
          </div>

          <div>
            <Label>Pasarela</Label>
            <select
              value={providerId}
              onChange={(e) => { setProviderId(e.target.value); setConfig({}) }}
              className="w-full bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neutral-600"
              disabled={mode === 'edit'}
            >
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {provider && <p className="text-xs text-neutral-500 mt-1">{provider.description}</p>}
          </div>

          {/* Campos dinámicos según configSchema del provider */}
          {provider && provider.configSchema.length > 0 && (
            <div className="space-y-3 border-t border-neutral-800 pt-4">
              <div className="flex items-center gap-2 text-xs text-neutral-400">
                <ArrowDown className="size-3" />
                <span>API keys / configuración del provider</span>
              </div>
              {provider.configSchema.map((field) => (
                <ConfigField
                  key={field.key}
                  field={field}
                  value={config[field.key] ?? ''}
                  onChange={(v) => setConfig((c) => ({ ...c, [field.key]: v }))}
                />
              ))}
              <p className="text-[11px] text-neutral-500 leading-relaxed bg-neutral-900/50 rounded p-2 border border-neutral-800">
                Al editar un método existente la configuración se sobreescribe con los valores que dejes en este formulario. Si dejas un campo en blanco se guardará vacío. Si no quieres tocar la config, no edites el método (solo activación / desactivación).
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="size-4 rounded border-neutral-700 bg-neutral-900"
            />
            <label htmlFor="isActive" className="text-sm">Marcar como pasarela activa (desactiva las demás)</label>
          </div>

          {save.error && (
            <div className="p-3 rounded bg-red-900/20 border border-red-800 text-sm text-red-400">{(save.error as Error).message}</div>
          )}
          <div className="flex gap-2 justify-end pt-2 border-t border-neutral-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Guardando...' : (mode === 'create' ? 'Agregar' : 'Guardar')}</Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

function ConfigField({ field, value, onChange }: {
  field: ProviderConfigField
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div>
      <Label>
        {field.label} {field.required && <span className="text-red-400">*</span>}
      </Label>
      {field.type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-neutral-900 border border-neutral-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neutral-600"
        >
          <option value="">— elegir —</option>
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <Input
          type={field.type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
        />
      )}
      {field.helpText && <p className="text-xs text-neutral-500 mt-1">{field.helpText}</p>}
    </div>
  )
}

function TransactionsTab({ data, isLoading, providerById }: {
  data: TransactionsResponse | undefined
  isLoading: boolean
  providerById: Map<string, ProviderInfo>
}) {
  if (isLoading) return <p className="text-sm text-neutral-500">Cargando...</p>
  const list = data?.data ?? []
  const stats = data?.stats ?? { total: 0, pending: 0, completed: 0, failed: 0, refunded: 0, volumeCompleted: 0 }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total transacciones" value={stats.total} />
        <Kpi label="Aprobadas" value={stats.completed} color="text-emerald-400" />
        <Kpi label="Fallidas" value={stats.failed} color="text-red-400" />
        <Kpi label="Volumen aprobado" value={formatCop(stats.volumeCompleted)} isMoney />
      </div>

      {list.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-neutral-500">
            Aún no hay transacciones. Cuando recibas un checkout aparecerán acá.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950 border-b border-neutral-800 text-xs text-neutral-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Orden</th>
                  <th className="text-left px-4 py-3">Provider tx</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Monto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {list.map((t) => {
                  const meta = t.metadata as Record<string, unknown>
                  const providerKey = String(meta.provider ?? '')
                  const providerName = providerById.get(providerKey)?.name ?? providerKey
                  return (
                    <tr key={t.id} className="hover:bg-neutral-900/50">
                      <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">{new Date(t.createdAt).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="px-4 py-3 font-mono text-xs">{t.orderNumber ?? '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className="text-neutral-300">{providerName || '—'}</span>
                        {t.providerTransactionId && <span className="block text-[10px] text-neutral-500 truncate max-w-[200px]">{t.providerTransactionId}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={t.status === 'completed' ? 'success' : t.status === 'failed' ? 'destructive' : t.status === 'refunded' ? 'muted' : 'warning'}>
                          {t.status === 'completed' ? 'Aprobada' : t.status === 'failed' ? 'Fallida' : t.status === 'refunded' ? 'Reembolsada' : 'Pendiente'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatCop(t.amount)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function Kpi({ label, value, color, isMoney }: { label: string; value: number | string; color?: string; isMoney?: boolean }) {
  return (
    <Card className="p-5">
      <p className="text-xs text-neutral-400 uppercase tracking-wider">{label}</p>
      <p className={cn(isMoney ? 'text-xl font-bold tabular-nums mt-1' : 'text-3xl font-bold tabular-nums mt-1', color)}>{value}</p>
    </Card>
  )
}

function formatCop(amount: number): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(amount / 100)
}
